import { useEffect, useId, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { Clock, Keyboard, Loader2, Search, TrendingUp, X } from 'lucide-react';
import type { Suggestion } from '#shared/types/public';
import { VirtualKeyboard } from './VirtualKeyboard';

/* Recente zoekopdrachten van DEZE bezoeker, in de browser. Hoogstens zes, de
   nieuwste bovenaan, zonder dubbelen. Alleen in localStorage: het is zijn
   eigen geheugen, niet iets dat de server hoeft te weten. */
const RECENT_KEY = 'kb:recent-searches';
const RECENT_MAX = 6;

function leesRecent(): string[] {
  try {
    const raw = JSON.parse(localStorage.getItem(RECENT_KEY) || '[]');
    return Array.isArray(raw) ? raw.filter((s) => typeof s === 'string').slice(0, RECENT_MAX) : [];
  } catch { return []; }
}
export function onthoudZoekopdracht(q: string) {
  const term = q.trim();
  if (term.length < 2) return;
  try {
    const lijst = [term, ...leesRecent().filter((s) => s.toLowerCase() !== term.toLowerCase())].slice(0, RECENT_MAX);
    localStorage.setItem(RECENT_KEY, JSON.stringify(lijst));
  } catch { /* privémodus */ }
}
function vergeetZoekopdracht(q: string): string[] {
  try {
    const lijst = leesRecent().filter((s) => s !== q);
    localStorage.setItem(RECENT_KEY, JSON.stringify(lijst));
    return lijst;
  } catch { return []; }
}

/** Zoekveld met autocomplete. Vervangt de TWEE verschillende implementaties
 *  van de EJS-app (home met lokale fuzzy-fallback, results zonder toetsenbord
 *  of toetsnavigatie) door één ARIA-correcte combobox: focus blijft in het
 *  invoerveld en de actieve optie wordt met aria-activedescendant aangewezen —
 *  de oude code verplaatste DOM-focus, waardoor doortypen stokte.
 *
 *  Is het veld LEEG, dan toont de lijst wat deze bezoeker eerder zocht en wat
 *  anderen vaak zoeken (`popular`, uit de zoeklog). Zo hoef je niet te
 *  verzinnen hoe iets heet voordat je kunt beginnen. */
export function SearchBox({
  defaultValue = '',
  size = 'lg',
  withKeyboard = false,
  autoFocus = false,
  popular = [],
}: {
  defaultValue?: string;
  size?: 'lg' | 'md';
  withKeyboard?: boolean;
  autoFocus?: boolean;
  /** Veelgezochte termen (met resultaat), uit de zoeklog. */
  popular?: string[];
}) {
  const navigate = useNavigate();
  const inputRef = useRef<HTMLInputElement>(null);
  const boxRef = useRef<HTMLDivElement>(null);
  const listId = useId();

  const [value, setValue] = useState(defaultValue);
  const [items, setItems] = useState<Suggestion[]>([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [active, setActive] = useState(-1);
  const [kbOpen, setKbOpen] = useState(false);
  // Pas ná het monteren uit localStorage: tijdens SSR bestaat die niet, en een
  // afwijkende beginwaarde zou een hydratatieverschil opleveren.
  const [recent, setRecent] = useState<string[]>([]);
  useEffect(() => { setRecent(leesRecent()); }, []);

  // Een nieuwe pagina met een andere zoekterm (zoekpagina → zoekpagina via
  // "Bedoelde je") moet het veld meenemen; zonder dit bleef de oude term staan.
  useEffect(() => { setValue(defaultValue); }, [defaultValue]);

  /* Suggesties horen bij TYPEN, niet bij landen.
   *
   * Deze effect draaide ook bij het monteren, en op /search?q=kassa staat er
   * dan al een waarde van twee tekens of meer in het veld. Gevolg: de
   * suggestielijst klapte vanzelf open bovenop de zoekresultaten — een lijst
   * van dezelfde artikelen die eronder al staan. Vandaar deze vlag: pas als
   * de bezoeker iets in het veld doet, gaan we suggereren. */
  const getypt = useRef(false);

  // debounce 210 ms — zelfde gevoel als het origineel
  useEffect(() => {
    const q = value.trim();
    if (!getypt.current) return;
    if (q.length < 2) { setItems([]); setLoading(false); setActive(-1); return; }
    setLoading(true);
    const ctl = new AbortController();
    const t = setTimeout(async () => {
      try {
        const res = await fetch(`/api/suggest?q=${encodeURIComponent(q)}`, { signal: ctl.signal });
        const data = await res.json();
        setItems(data.suggestions || []);
        setOpen(true);
        setActive(-1);
      } catch { /* afgebroken */ }
      finally { setLoading(false); }
    }, 210);
    return () => { clearTimeout(t); ctl.abort(); };
  }, [value]);

  // klik buiten sluit
  useEffect(() => {
    const onDoc = (e: MouseEvent) => {
      if (boxRef.current && !boxRef.current.contains(e.target as Node)) { setOpen(false); setKbOpen(false); }
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  // viewTransition: dezelfde vloeier als bij een <Link>. Zonder deze optie
  // zou juist de zoekbalk — de meest gebruikte sprong van de site — als enige
  // hard omslaan.
  const submit = (q: string) => {
    if (!q) { navigate('/search', { viewTransition: true }); setOpen(false); return; }
    onthoudZoekopdracht(q);
    setOpen(false);
    navigate(`/search?q=${encodeURIComponent(q)}`, { viewTransition: true });
  };
  const goto = (s: Suggestion) => {
    onthoudZoekopdracht(value.trim());
    setOpen(false);
    navigate(`/artikel/${encodeURIComponent(s.slug || s.id)}?q=${encodeURIComponent(value.trim())}`, { viewTransition: true });
  };

  /* Twee standen van de lijst:
     - mét tekst: artikelsuggesties + "Alle resultaten bekijken" als laatste;
     - zonder tekst: recente en veelgezochte termen (elk een zoekopdracht).
     Beide lopen door dezelfde optie-index, zodat de pijltjes overal werken. */
  const leeg = value.trim().length < 2;
  const termOpties = leeg
    ? [
        ...recent.map((t) => ({ term: t, soort: 'recent' as const })),
        ...popular.filter((p) => !recent.some((r) => r.toLowerCase() === p.toLowerCase())).slice(0, 6)
          .map((t) => ({ term: t, soort: 'populair' as const })),
      ]
    : [];
  const optionCount = leeg ? termOpties.length : (items.length > 0 ? items.length + 1 : 0);
  const chooseAt = (i: number) => {
    if (leeg) { const o = termOpties[i]; if (o) submit(o.term); return; }
    if (i === items.length) submit(value.trim());
    else if (items[i]) goto(items[i]!);
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      if (kbOpen) { setKbOpen(false); return; }
      if (open) { setOpen(false); return; }
      // Tweede Esc: het veld leegmaken, zodat je zonder muis opnieuw kunt beginnen.
      if (value) { getypt.current = true; setValue(''); }
      return;
    }
    if (!open || optionCount === 0) return;
    if (e.key === 'ArrowDown') { e.preventDefault(); setActive((i) => (i + 1) % optionCount); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setActive((i) => (i - 1 + optionCount) % optionCount); }
    else if (e.key === 'Home') { e.preventDefault(); setActive(0); }
    else if (e.key === 'End') { e.preventDefault(); setActive(optionCount - 1); }
    else if (e.key === 'Enter' && active >= 0) { e.preventDefault(); chooseAt(active); }
    else if (e.key === 'Tab') setOpen(false);
  };

  // lg = het hero-zoekveld. Pilvorm, want de hele hero spreekt nu in pillen:
  // het bovenschrift, de themachips en de accentbalk zijn alle drie
  // rounded-full. Een kader met 18px-radius middenin die taal las als een los
  // onderdeel dat er niet bij hoorde. md houdt de kaartvorm.
  const inputCls =
    size === 'lg'
      ? 'h-14 rounded-full pl-14 pr-24 text-lg shadow-md'
      : 'h-12 rounded-lg pl-11 pr-12 text-lg shadow-sm';

  const toonLijst = open && optionCount > 0;

  return (
    <div ref={boxRef} className="relative w-full">
      <form
        role="search"
        onSubmit={(e) => { e.preventDefault(); submit(value.trim()); }}
      >
        <Search
          size={size === 'lg' ? 20 : 17}
          className={`pointer-events-none absolute top-1/2 -translate-y-1/2 text-ink-3 ${size === 'lg' ? 'left-5.5' : 'left-3.5'}`}
          aria-hidden
        />
        <input
          ref={inputRef}
          type="search"
          name="q"
          value={value}
          autoFocus={autoFocus}
          onChange={(e) => { getypt.current = true; setValue(e.target.value); if (e.target.value.trim().length < 2) setOpen(true); }}
          onKeyDown={onKeyDown}
          onFocus={() => { setRecent(leesRecent()); setOpen(true); }}
          placeholder="Waar ben je naar op zoek?"
          aria-label="Zoeken in de kennisbank"
          role="combobox"
          aria-expanded={toonLijst}
          // Alleen verwijzen als de lijst er echt is: bij nul suggesties
          // bestaat het element niet en wijst aria-controls naar een dood id.
          aria-controls={toonLijst ? listId : undefined}
          aria-autocomplete="list"
          aria-activedescendant={active >= 0 ? `${listId}-opt-${active}` : undefined}
          autoComplete="off"
          autoCorrect="off"
          spellCheck={false}
          data-gramm="false"
          data-kb-search
          className={`w-full border border-hairline-strong bg-raised text-ink transition placeholder:text-ink-3 focus:border-accent focus:ring-3 focus:ring-accent/20 ${inputCls}`}
        />
        <div className="absolute top-1/2 right-3 flex -translate-y-1/2 items-center gap-1">
          {loading && <Loader2 size={16} className="animate-spin text-ink-3" aria-hidden />}
          {withKeyboard && (
            <button
              type="button"
              onClick={() => { setKbOpen((v) => !v); inputRef.current?.focus(); }}
              aria-pressed={kbOpen}
              aria-label="Schermtoetsenbord"
              title="Schermtoetsenbord"
              className={`rounded-md p-2 transition ${kbOpen ? 'bg-accent-soft text-accent-soft-ink' : 'text-ink-3 hover:bg-surface-hover hover:text-ink'}`}
            >
              <Keyboard size={17} />
            </button>
          )}
        </div>
      </form>

      {/* Listbox volgens het combobox-patroon: de focus blijft in het
          invoerveld en de actieve optie wordt met aria-activedescendant
          aangewezen. Opties mogen dan GEEN focusbare elementen bevatten —
          een <button> per optie levert nested-interactive op en laat
          schermlezers de rij als knop aankondigen in plaats van als optie.
          Om dezelfde reden staan alleen role="option"-kinderen in de lijst;
          de lege staat leeft eronder, buiten de listbox. */}
      {toonLijst && !leeg && (
        <ul
          id={listId}
          role="listbox"
          aria-label="Zoeksuggesties"
          className="absolute top-full right-0 left-0 z-50 mt-2 max-h-96 overflow-auto rounded-lg border border-hairline bg-raised py-1 shadow-lg motion-safe:animate-fade-down"
        >
          {items.map((s, i) => (
            <li
              key={s.id}
              id={`${listId}-opt-${i}`}
              role="option"
              aria-selected={i === active}
              onMouseEnter={() => setActive(i)}
              onMouseDown={(e) => { e.preventDefault(); goto(s); }}
              className={`flex cursor-pointer items-start gap-3 px-4 py-2.5 transition ${i === active ? 'bg-surface-hover' : ''}`}
            >
              <span className="min-w-0 flex-1">
                <span className="block truncate text-base font-medium text-ink">{s.name}</span>
                {s.subject && s.subject !== s.name && <span className="block truncate text-sm text-ink-3">{s.subject}</span>}
              </span>
              {typeof s.score === 'number' && s.score > 0 && (
                <span className="mt-0.5 shrink-0 rounded-full bg-accent-soft px-2 py-0.5 text-2xs font-bold text-accent-soft-ink">
                  {s.score}%
                </span>
              )}
            </li>
          ))}
          <li
            id={`${listId}-opt-${items.length}`}
            role="option"
            aria-selected={active === items.length}
            onMouseEnter={() => setActive(items.length)}
            onMouseDown={(e) => { e.preventDefault(); submit(value.trim()); }}
            className={`cursor-pointer border-t border-hairline px-4 py-2.5 text-base font-semibold text-ink transition ${
              active === items.length ? 'bg-surface-hover' : ''
            }`}
          >
            Alle resultaten bekijken →
          </li>
        </ul>
      )}

      {toonLijst && leeg && (
        <ul
          id={listId}
          role="listbox"
          aria-label="Eerdere en veelgezochte zoekopdrachten"
          className="absolute top-full right-0 left-0 z-50 mt-2 max-h-96 overflow-auto rounded-lg border border-hairline bg-raised py-1 text-left shadow-lg motion-safe:animate-fade-down"
        >
          {termOpties.map((o, i) => {
            const eersteVanSoort = i === 0 || termOpties[i - 1]!.soort !== o.soort;
            return (
              <li
                key={`${o.soort}-${o.term}`}
                id={`${listId}-opt-${i}`}
                role="option"
                aria-selected={i === active}
                onMouseEnter={() => setActive(i)}
                onMouseDown={(e) => { e.preventDefault(); submit(o.term); }}
                className={`group/opt cursor-pointer px-4 py-2 transition ${i === active ? 'bg-surface-hover' : ''} ${eersteVanSoort && i > 0 ? 'mt-1 border-t border-hairline pt-3' : ''}`}
              >
                {eersteVanSoort && (
                  <span className="mb-1 block text-2xs font-semibold tracking-wider text-ink-3 uppercase">
                    {o.soort === 'recent' ? 'Eerder gezocht' : 'Vaak gezocht'}
                  </span>
                )}
                <span className="flex items-center gap-2.5">
                  {o.soort === 'recent'
                    ? <Clock size={14} className="shrink-0 text-ink-3" aria-hidden />
                    : <TrendingUp size={14} className="shrink-0 text-accent" aria-hidden />}
                  <span className="min-w-0 flex-1 truncate text-base text-ink">{o.term}</span>
                  {o.soort === 'recent' && (
                    /* Een <span> met role=button en geen <button>: binnen een
                       option mag geen focusbaar element staan. Bedienbaar met
                       de muis; met het toetsenbord wis je via de browser. */
                    <span
                      role="button"
                      aria-label={`"${o.term}" vergeten`}
                      title="Vergeten"
                      onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); setRecent(vergeetZoekopdracht(o.term)); }}
                      className="shrink-0 rounded-md p-1 text-ink-3 opacity-0 transition group-hover/opt:opacity-100 hover:bg-sunken hover:text-ink"
                    >
                      <X size={13} aria-hidden />
                    </span>
                  )}
                </span>
              </li>
            );
          })}
        </ul>
      )}

      {open && !leeg && optionCount === 0 && !loading && (
        <p
          role="status"
          className="absolute top-full right-0 left-0 z-50 mt-2 rounded-lg border border-hairline bg-raised px-4 py-3 text-base text-ink-3 shadow-lg"
        >
          Geen suggesties gevonden — druk op Enter om in de hele tekst te zoeken.
        </p>
      )}

      {kbOpen && (
        <VirtualKeyboard
          onKey={(k) => {
            const el = inputRef.current;
            if (!el) return;
            const start = el.selectionStart ?? value.length;
            const end = el.selectionEnd ?? value.length;
            let next = value;
            let caret = start;
            if (k === '\b') { next = value.slice(0, Math.max(0, start - (start === end ? 1 : 0))) + value.slice(end); caret = Math.max(0, start - 1); }
            else if (k === '\x00') { next = ''; caret = 0; }
            else { next = value.slice(0, start) + k + value.slice(end); caret = start + k.length; }
            getypt.current = true;
            setValue(next);
            requestAnimationFrame(() => { el.focus(); el.setSelectionRange(caret, caret); });
          }}
          onClose={() => { setKbOpen(false); inputRef.current?.focus(); }}
        />
      )}
    </div>
  );
}
